import React, { useRef, useState } from "react";

import { IgButton, IgPaper, IgTypography } from "@intelligems/ig-theme";
import { BreakpointIconId } from "@intelligems/ig-types";
import { ProgressBarIcon } from "@intelligems/ig-widgets/ProgressBarView/icons/ProgressBarIcon";
import { Box, Grid, Popover, useTheme } from "@mui/material";
import { useProgressBarTierVariable } from "@src/store/slices/userInterfaces/hooks/useProgressBarTierVariable";
import { useProgressBarVariable } from "@src/store/slices/userInterfaces/hooks/useProgressBarVariable";

type IconRowProps = {
  iconId: string;
  label: string;
  isActive: boolean;
  onChange: (id: BreakpointIconId) => void;
  activeIconColor?: string;
  inactiveIconColor?: string;
  activeBackgroundColor?: string;
  inactiveBackgroundColor?: string;
};

export const IconRow = ({
  iconId,
  label,
  isActive,
  onChange,
  activeIconColor,
  inactiveIconColor,
  activeBackgroundColor,
  inactiveBackgroundColor,
}: IconRowProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const theme = useTheme();

  const handleChangeClick = () => {
    setAnchorEl(buttonRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleIconSelect = (newIconId: BreakpointIconId) => {
    onChange(newIconId);
    handleClose();
  };

  return (
    <IgPaper
      sx={{
        p: 5,
        // width: "100%",
      }}
    >
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        wrap={"nowrap"}
      >
        <Grid
          item
          container
          alignItems="center"
          columnSpacing={5}
          wrap={"nowrap"}
        >
          <Grid item>
            <Box
              sx={{
                width: 48,
                height: 48,
                border: `1px solid ${theme.igBorders.core.default}`,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: isActive
                  ? activeBackgroundColor ||
                    theme.igBackgroundColors.interactive.neutral.active
                  : inactiveBackgroundColor ||
                    theme.igBackgroundColors.interactive.neutral.default,
              }}
            >
              <ProgressBarIcon
                color={
                  isActive
                    ? activeIconColor || theme.palette.common.white
                    : inactiveIconColor || theme.palette.common.black
                }
                id={iconId as BreakpointIconId}
              />
            </Box>
          </Grid>
          <Grid item>
            <IgTypography variant="body1">{label}</IgTypography>
          </Grid>
        </Grid>
        <Grid item>
          <IgButton ref={buttonRef} color="link" onClick={handleChangeClick}>
            Change
          </IgButton>
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
              }}
            >
              {options.map((option) => (
                <Box
                  key={option}
                  sx={{
                    width: 44,
                    height: 44,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "4px",
                    cursor: "pointer",
                    "&:hover": {
                      cursor: "pointer",
                      backgroundColor:
                        theme.igBackgroundColors.interactive.neutral.hover,
                    },
                  }}
                  onClick={() => handleIconSelect(option)}
                >
                  <ProgressBarIcon color="default" id={option} />
                </Box>
              ))}
            </Box>
          </Popover>
        </Grid>
      </Grid>
    </IgPaper>
  );
};

export const IconsSection = ({
  interfaceId,
  tierId,
  activeIconColor,
  inactiveIconColor,
  activeBackgroundColor,
  inactiveBackgroundColor,
}: {
  interfaceId: string;
  tierId: string;
  activeIconColor?: string;
  inactiveIconColor?: string;
  activeBackgroundColor?: string;
  inactiveBackgroundColor?: string;
}) => {
  const activeIconId = useProgressBarTierVariable(
    interfaceId,
    tierId,
    "activeIconId"
  );
  const inactiveIconId = useProgressBarTierVariable(
    interfaceId,
    tierId,
    "inactiveIconId"
  );

  return (
    <Grid item container direction="column" spacing={3}>
      <Grid item>
        <IgTypography fontWeight="600">Icons</IgTypography>
      </Grid>
      <Grid
        container
        item
        direction="row"
        columnSpacing={3}
        alignItems="center"
        justifyContent="space-between"
      >
        <Grid item xs={6}>
          <IconRow
            iconId={inactiveIconId[0] ?? "lockClosed"}
            label="Breakpoint inactive"
            isActive={false}
            onChange={inactiveIconId[1]}
            activeIconColor={activeIconColor}
            inactiveIconColor={inactiveIconColor}
            activeBackgroundColor={activeBackgroundColor}
            inactiveBackgroundColor={inactiveBackgroundColor}
          />
        </Grid>
        <Grid item xs={6}>
          <IconRow
            iconId={activeIconId[0] ?? "check"}
            label="Breakpoint active"
            isActive={true}
            onChange={activeIconId[1]}
            activeIconColor={activeIconColor}
            inactiveIconColor={inactiveIconColor}
            activeBackgroundColor={activeBackgroundColor}
            inactiveBackgroundColor={inactiveBackgroundColor}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export const ShippingProgressBarIconsSection = ({
  interfaceId,
  activeIconColor,
  inactiveIconColor,
  activeBackgroundColor,
  inactiveBackgroundColor,
}: {
  interfaceId: string;
  activeIconColor?: string;
  inactiveIconColor?: string;
  activeBackgroundColor?: string;
  inactiveBackgroundColor?: string;
}) => {
  const activeIconId = useProgressBarVariable(
    interfaceId,
    "textReplacements.activeIconId"
  );
  const inactiveIconId = useProgressBarVariable(
    interfaceId,
    "textReplacements.inactiveIconId"
  );

  return (
    <Grid item container direction="column" spacing={3}>
      <Grid item>
        <IgTypography fontWeight="600">Icons</IgTypography>
      </Grid>
      <Grid
        container
        item
        direction="row"
        columnSpacing={3}
        alignItems="center"
        justifyContent="space-between"
      >
        <Grid item xs={6}>
          <IconRow
            iconId={inactiveIconId[0] ?? "lockClosed"}
            label="Breakpoint inactive"
            isActive={false}
            onChange={inactiveIconId[1]}
            activeIconColor={activeIconColor}
            inactiveIconColor={inactiveIconColor}
            activeBackgroundColor={activeBackgroundColor}
            inactiveBackgroundColor={inactiveBackgroundColor}
          />
        </Grid>
        <Grid item xs={6}>
          <IconRow
            iconId={activeIconId[0] ?? "check"}
            label="Breakpoint active"
            isActive={true}
            onChange={activeIconId[1]}
            activeIconColor={activeIconColor}
            inactiveIconColor={inactiveIconColor}
            activeBackgroundColor={activeBackgroundColor}
            inactiveBackgroundColor={inactiveBackgroundColor}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

const options: BreakpointIconId[] = [
  "lockOpen",
  "check",
  "cartAdd",
  "cart",
  "truck",
  "crown",
  "label",
  "gift",
  "sun",
  "snowflake",
  "lightning",
  "star",
  "thumbsUp",
  "smileyDark",
  "smileyLight",
  "plus",
  "lockClosed",
  "key",
];
